import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import {
    SCORE_TEAM_CATEGORIES,
    SCORE_TEAM_MIN_MEMBERS,
    SCORE_TEAM_MAX_MEMBERS,
    scoreTeamCategoryLabel,
    weaponAbbr,
} from '../../lib/conventionResults';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

// Admin editor for Lagresultater teams (convention.scoreTeams): free-form
// teams of 2-4 shooters with a name and a manually chosen category, assembled
// from the note shooters hand in. A member is a specific start (participant +
// weapon class), so a shooter with several starts appears once per start.
// Distinct from teamEditor.js (NSF lagskyting with fixed team sizes).
@observer
class ScoreTeamEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            category: 'open',
            memberKeys: [],
            search: '',
            saving: false,
            error: '',
        };
    }

    handleField = (field) => (e) => {
        this.setState({ [field]: e.target.value, error: '' });
    }

    toggleMember = (key) => () => {
        const { memberKeys } = this.state;
        if (memberKeys.includes(key)) {
            this.setState({ memberKeys: memberKeys.filter(x => x !== key), error: '' });
        } else if (memberKeys.length < SCORE_TEAM_MAX_MEMBERS) {
            this.setState({ memberKeys: [...memberKeys, key], error: '' });
        } else {
            this.setState({ error: `Maks ${SCORE_TEAM_MAX_MEMBERS} skyttere på et lag` });
        }
    }

    // Map of participantId → existing team name. A person can be on ONE team
    // only, so every start of an already-assigned shooter is locked.
    personAssignments() {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const out = new Map();
        (convention?.scoreTeams || []).forEach(t => {
            (t.members || []).forEach(m => {
                out.set(parseInt(m.participantId, 10), t.name || '(uten navn)');
            });
        });
        return out;
    }

    saveTeam = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { name, category, memberKeys } = this.state;
        if (!name.trim()) { this.setState({ error: 'Lagnavn må fylles ut' }); return; }
        if (memberKeys.length < SCORE_TEAM_MIN_MEMBERS || memberKeys.length > SCORE_TEAM_MAX_MEMBERS) {
            this.setState({ error: `Lag må ha ${SCORE_TEAM_MIN_MEMBERS}–${SCORE_TEAM_MAX_MEMBERS} skyttere` });
            return;
        }
        const starts = this.collectStarts();
        // Reject if any chosen shooter is already on another team (one person,
        // one team — regardless of which start was picked).
        const assignments = this.personAssignments();
        const conflict = memberKeys.find(key => assignments.has(parseInt(key, 10)));
        if (conflict !== undefined) {
            const s = starts.find(st => st.key === conflict);
            const who = s ? `${s.firstname} ${s.lastname}`.trim() : conflict;
            this.setState({ error: `${who} er allerede på laget «${assignments.get(parseInt(conflict, 10))}»` });
            return;
        }
        // Reject the same shooter twice on one team (two different starts).
        const personIds = memberKeys.map(key => parseInt(key, 10));
        if (new Set(personIds).size !== personIds.length) {
            this.setState({ error: 'Samme skytter kan ikke stå to ganger på laget' });
            return;
        }
        const members = memberKeys.map(key => {
            const s = starts.find(st => st.key === key);
            return {
                participantId: s ? s.id : parseInt(key, 10),
                weapon: s ? s.weapon : '',
                name: s ? `${s.firstname} ${s.lastname}`.trim() : '',
                sportsClub: s?.sportsClub || '',
            };
        });

        this.setState({ saving: true, error: '' });
        try {
            await conventionStore.addArrayObject({
                newObject: { name: name.trim(), category, members },
                object: convention,
                field: 'scoreTeams',
                objectName: 'convention',
            });
            this.setState({ name: '', memberKeys: [], saving: false });
        } catch (err) {
            this.setState({ saving: false, error: err?.message || 'Klarte ikke å lagre lag' });
        }
    }

    removeTeam = (teamMd5) => async () => {
        const dialogAction = beginDialogAction(this, 'dialog:112', () => [
            this.props?.stores?.conventionStore?.convention?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { conventionStore } = this.props.stores;
            const { convention } = conventionStore;
            if (!((await appConfirm('Slette dette laget?')) && dialogAction.isCurrent())) return;
            await conventionStore.removeArrayObject({
                field: 'scoreTeams',
                id: convention.id,
                md5: teamMd5,
                objectName: 'convention',
            });
        } finally {
            dialogAction.finish();
        }
    }

    // Flat, deduplicated list of starts (shooter + weapon class) known to this
    // convention. One row per participants{N} entry (a shooter with fin AND grov
    // starts appears twice, once per weapon); shooters only on the main signup
    // roster (no stage yet) appear once with no weapon — scores then resolve to
    // their best start.
    collectStarts() {
        const { conventionStore } = this.props.stores;
        const { convention, participants } = conventionStore;
        const all = new Map();
        Object.keys(convention || {}).forEach(key => {
            if (!/^participants\d+$/.test(key)) return;
            (convention[key] || []).forEach(sp => {
                const startKey = `${sp.id}::${sp.class || ''}`;
                if (all.has(startKey)) return;
                const u = (participants || []).find(p => p.id === sp.id) || {};
                all.set(startKey, {
                    key: startKey,
                    id: sp.id,
                    weapon: sp.class || '',
                    firstname: sp.firstname || u.firstname || '',
                    lastname: sp.lastname || u.lastname || '',
                    sportsClub: u.sportsClub || '',
                    gender: sp.gender || '',
                });
            });
        });
        const onStage = new Set(Array.from(all.values()).map(s => s.id));
        (convention?.participants || []).forEach(sp => {
            if (onStage.has(sp.id)) return;
            const startKey = `${sp.id}::`;
            if (all.has(startKey)) return;
            const u = (participants || []).find(p => p.id === sp.id) || {};
            all.set(startKey, {
                key: startKey,
                id: sp.id,
                weapon: '',
                firstname: sp.firstname || u.firstname || '',
                lastname: sp.lastname || u.lastname || '',
                sportsClub: u.sportsClub || '',
                gender: sp.gender || '',
            });
        });
        return Array.from(all.values());
    }

    render() {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;

        if (!convention) return null;

        const { name, category, memberKeys, search, saving, error } = this.state;
        const starts = this.collectStarts();
        const filterText = search.trim().toLowerCase();
        const visibleStarts = filterText
            ? starts.filter(s => `${s.firstname} ${s.lastname} ${s.sportsClub}`.toLowerCase().includes(filterText))
            : starts;
        const teams = (convention.scoreTeams || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const assignments = this.personAssignments();
        const selectedPersonIds = memberKeys.map(key => parseInt(key, 10));

        return (
            <div class='p-3'>
                <h4 class='mb-3'>
                    <Text id='convention.score-team-editor-title'>Manage result teams</Text>
                </h4>

                <div class='p-3 mb-4 rounded bg-body-tertiary'>
                    <h6 class='mb-3'><Text id='convention.score-team-add'>Create team</Text></h6>

                    <div class='mb-2'>
                        <Localizer>
                            <input
                                type='text'
                                class='form-control rounded-pill'
                                placeholder={<Text id='convention.team-name-placeholder'>Team name (e.g. Dokka A)</Text>}
                                value={name}
                                onInput={this.handleField('name')}
                            />
                        </Localizer>
                    </div>

                    <div class='mb-2'>
                        <select
                            class='form-control rounded-pill'
                            value={category}
                            onInput={this.handleField('category')}
                        >
                            {SCORE_TEAM_CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>

                    <div class='mb-2'>
                        <Localizer>
                            <input
                                type='search'
                                class='form-control form-control-sm rounded-pill'
                                placeholder={<Text id='convention.team-search-shooter'>Search shooter…</Text>}
                                value={search}
                                onInput={this.handleField('search')}
                            />
                        </Localizer>
                    </div>

                    <div class='small text-body-secondary mb-1'>
                        {memberKeys.length} / {SCORE_TEAM_MIN_MEMBERS}–{SCORE_TEAM_MAX_MEMBERS}
                        <Text id='convention.team-members-selected'> selected</Text>
                    </div>

                    <div class='border rounded' style='max-height: 280px; overflow-y: auto;'>
                        {visibleStarts.length === 0 ? (
                            <div class='p-3 text-body-secondary small'>
                                <Text id='convention.score-team-no-shooters'>No shooters match.</Text>
                            </div>
                        ) : visibleStarts.map(s => {
                            const isChecked = memberKeys.includes(s.key);
                            const assignedTo = assignments.get(s.id);
                            const tooMany = !isChecked && memberKeys.length >= SCORE_TEAM_MAX_MEMBERS;
                            // One person, one team: another start of an already
                            // ticked shooter is locked too.
                            const personSelected = !isChecked && selectedPersonIds.includes(s.id);
                            const disabled = !isChecked && (tooMany || !!assignedTo || personSelected);
                            const genderBadge = s.gender === 'k' ? 'K' : (s.gender === 'm' ? 'M' : '');
                            return (
                                <label
                                    key={s.key}
                                    class={`d-flex align-items-center px-2 py-1 border-bottom ${disabled ? 'text-body-secondary' : ''}`}
                                    style={`cursor: ${disabled ? 'not-allowed' : 'pointer'};`}
                                    title={assignedTo ? `Allerede på laget «${assignedTo}»` : (personSelected ? 'Allerede valgt på dette laget' : '')}
                                >
                                    <input
                                        type='checkbox'
                                        class='form-check-input me-2'
                                        checked={isChecked}
                                        disabled={disabled}
                                        onChange={this.toggleMember(s.key)}
                                    />
                                    <span class='flex-grow-1'>
                                        {s.firstname} {s.lastname}
                                        <span class='small text-body-secondary ms-2'>{s.sportsClub}</span>
                                    </span>
                                    {assignedTo && (
                                        <span class='badge text-bg-secondary ms-1' style='font-size: 0.65em;'>
                                            <i class='fa-regular fa-lock me-1' />{assignedTo}
                                        </span>
                                    )}
                                    {weaponAbbr(s.weapon) && (
                                        <span class='badge text-bg-secondary ms-1' style='font-size: 0.65em;'>{weaponAbbr(s.weapon)}</span>
                                    )}
                                    {genderBadge && (
                                        <span class='badge text-bg-secondary ms-1' style='font-size: 0.65em;'>{genderBadge}</span>
                                    )}
                                </label>
                            );
                        })}
                    </div>

                    {error && (
                        <div class='alert alert-warning small mt-2 mb-0 py-2'>{error}</div>
                    )}

                    <button
                        type='button'
                        class='btn btn-primary rounded-pill mt-3 w-100'
                        disabled={saving}
                        onClick={this.saveTeam}
                    >
                        {saving
                            ? <Text id='general.saving'>Saving…</Text>
                            : <Text id='convention.team-save'>Save team</Text>}
                    </button>
                </div>

                <h6 class='mb-2'>
                    <Text id='convention.team-existing'>Existing teams</Text>
                    <span class='badge text-bg-secondary ms-2'>{teams.length}</span>
                </h6>
                {teams.length === 0 ? (
                    <div class='alert alert-info'>
                        <Text id='convention.no-score-teams-yet'>No teams created yet.</Text>
                    </div>
                ) : (
                    <table class='table table-sm align-middle'>
                        <thead>
                            <tr>
                                <th><Text id='convention.team-name'>Team</Text></th>
                                <th><Text id='convention.score-team-category'>Category</Text></th>
                                <th><Text id='convention.team-members'>Members</Text></th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {teams.map(t => (
                                <tr key={t.md5}>
                                    <td class='fw-semibold'>{t.name}</td>
                                    <td>
                                        <span class='badge text-bg-success'>{scoreTeamCategoryLabel(t.category)}</span>
                                    </td>
                                    <td class='small'>
                                        {(t.members || []).map(m => m.name).join(', ')}
                                    </td>
                                    <td class='text-end'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-danger'
                                            onClick={this.removeTeam(t.md5)}
                                        >
                                            <i class='fa-regular fa-trash' />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }
}

export default ScoreTeamEditor;
